// Role & capability mapping — mirrors backend ROLE_CAPABILITIES.
// Legacy roles are normalised: staff → order_team, karigar → production.

export const ROLE_LABELS = {
  "super-admin": "Super Admin",
  admin: "Admin",
  manager: "Manager",
  order_team: "Order Team",
  staff: "Order Team",
  sales: "Sales",
  production: "Production",
  karigar: "Production",
  accounts: "Accounts",
  client: "Client",
  cad_designer: "CAD Designer",
  watchman: "Watchman",
};

export const ROLE_DESCRIPTIONS = {
  "super-admin": "Platform owner. Manages all companies.",
  admin: "Business owner. Full access within the company.",
  manager: "Runs day-to-day. Sees everything except platform settings.",
  order_team: "Front-of-shop: creates orders, manages CAD/spec, updates production.",
  sales: "Manages customers and pipeline. Light order edits.",
  production: "Shop-floor. Sees only their department queue — no customer or pricing info.",
  accounts: "Ledger view: CAD payables and invoices.",
  client: "Customer. Read-only tracker for their own orders.",
};

export const ORDERED_ROLES = ["admin", "manager", "order_team", "sales", "accounts", "production", "client"];

// Normalise a role string
export function normRole(role) {
  if (role === "staff") return "order_team";
  if (role === "karigar") return "production";
  return role;
}

// Full capability matrix. Key = capability, Value = { roles: [role...] }
// Frontend uses this to gate UI. Backend enforces on API.
const MATRIX = {
  // Foundational
  "dashboard.view":        ["admin", "manager", "order_team", "sales", "accounts"],
  "search.global":         ["admin", "manager", "order_team", "sales", "accounts"],
  // Orders
  "orders.view":           ["admin", "manager", "order_team", "sales", "accounts", "client"],
  "orders.create":         ["admin", "manager", "order_team", "sales"],
  "orders.edit":           ["admin", "manager", "order_team"],
  "orders.delete":         ["admin", "manager"],
  "orders.update_status":  ["admin", "manager", "order_team"],
  "orders.see_client":     ["admin", "manager", "order_team", "sales", "accounts", "client"],
  "orders.see_pricing":    ["admin", "manager", "order_team", "sales", "accounts"],
  // CRM
  "contacts.view":         ["admin", "manager", "order_team", "sales"],
  "contacts.edit":         ["admin", "manager", "order_team", "sales"],
  "deals.view":            ["admin", "manager", "order_team", "sales"],
  "deals.edit":            ["admin", "manager", "order_team", "sales"],
  // Catalog
  "catalog.view":          ["admin", "manager", "order_team", "sales"],
  "catalog.edit":          ["admin", "manager", "order_team", "sales"],
  "catalog.delete":        ["admin", "manager"],
  "catalog.presentation":  ["admin", "manager", "order_team", "sales"],
  "catalog.bulk_import":   ["admin", "manager", "order_team"],
  "catalog.share":         ["admin", "manager", "order_team", "sales"],
  "catalog.shares.view":   ["admin", "manager", "order_team", "sales"],
  "collections.view":      ["admin", "manager", "order_team", "sales"],
  "collections.edit":      ["admin", "manager", "order_team", "sales"],
  "gold_rates.view":       ["admin", "manager", "order_team", "accounts"],
  "gold_rates.edit":       ["admin", "manager", "order_team", "accounts"],
  // Production
  "production.board":      ["admin", "manager", "order_team", "accounts"],
  "production.my_queue":   ["production"],
  "production.spec":       ["admin", "manager", "order_team"],
  "production.update":     ["admin", "manager", "order_team", "production"],
  "production.gate":       ["admin", "manager", "order_team", "production"],
  "production.see_client": ["admin", "manager", "order_team", "sales", "accounts"],
  // CAD & Invoices
  "cad.quotes.view":       ["admin", "manager", "order_team", "accounts"],
  "cad.quotes.edit":       ["admin", "manager", "order_team", "accounts"],
  "cad.files.view":        ["admin", "manager", "order_team", "accounts"],
  "cad.files.edit":        ["admin", "manager", "order_team", "accounts"],
  "cad.files.pay":         ["admin", "manager", "accounts"],
  // CAD Designer (9th role) — own queue only, upload own CAD files, negotiate own charges.
  "cad.designer.queue":    ["admin", "manager", "cad_designer"],
  "cad.designer.upload":   ["admin", "cad_designer"],
  "cad.designer.negotiate": ["admin", "manager", "order_team", "cad_designer"],
  "invoices.view":         ["admin", "manager", "order_team", "accounts"],
  "invoices.edit":         ["admin", "manager", "accounts"],
  // Settings & Admin
  "settings.view":         ["admin"],
  "settings.users":        ["admin"],
  "settings.roles":        ["admin", "manager"],
  // Client (read-only)
  "tracker.own":           ["client"],
  // Field Sales
  "field.own":             ["sales", "manager", "order_team"],
  "field.expenses.approve": ["manager"],
  "field.expenses.reimburse": ["accounts"],
  "field.team.view":       ["manager", "accounts"],
  // Assets, Procurement, Visitors
  "assets.view":           ["admin", "manager", "accounts", "production"],
  "assets.manage":         ["admin", "manager", "accounts"],
  "procurement.view":      ["admin", "manager", "accounts"],
  "procurement.inward":    ["admin", "manager", "accounts", "watchman"],
  "visitors.gate":         ["admin", "manager", "watchman"],
  "visitors.view":         ["admin", "manager", "watchman", "order_team", "sales", "accounts", "cad_designer"],
  // CAD Designer queue (own work) — designer + coordinators
  "cad.designer.queue":    ["cad_designer", "order_team", "manager"],
  // Platform (super-admin only)
  "platform.super_admin":  ["super-admin"],
};

export function can(user, capability) {
  if (!user) return false;
  const role = normRole(user.role);
  // Platform-level capabilities are super-admin only.
  if (capability && capability.startsWith("platform.")) {
    return role === "super-admin";
  }
  if (role === "admin" || role === "super-admin") return true;
  const allowed = MATRIX[capability];
  if (!allowed) return false;
  return allowed.includes(role);
}

// For the read-only permissions matrix screen
export function fullMatrix() {
  return Object.entries(MATRIX).map(([cap, roles]) => ({ capability: cap, roles }));
}

export function groupedMatrix() {
  const groups = {};
  Object.entries(MATRIX).forEach(([cap, roles]) => {
    const group = cap.split(".")[0];
    (groups[group] = groups[group] || []).push({ capability: cap, roles });
  });
  return groups;
}
